import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Alan, Buton, Secim } from "@koclukpanel/ui";
import { ApiHatasi, ogrenciProfilGuncelle, type HedefGirdisi, type OgrenciKunyesi } from "../api";
import { AlanSecimi } from "../AlanSecimi";

/**
 * HEDEF DÜZENLEME — F-PPL-05
 *
 * ================================================================
 * NEDEN PROFİL FORMUNDA DEĞİL, HEDEF KARTINDA
 * ================================================================
 * Hedef alanları `OgrenciDosyasi.tsx`teki profil formuna da
 * konabilirdi. Konmadı: o form verisini ÖĞRENCİ LİSTESİNDEN alıyor
 * (`OgrenciSatiri`) ve hedef alanları listede YOK — bilerek yok,
 * toplu bir listede hedef puan ve hedef okul gereksiz bir veri yüzeyi.
 *
 * Yani orada düzenlemek, listeye hedef alanları eklemeyi ya da ikinci
 * bir sorgu açmayı gerektirirdi. Künye zaten burada ve hedef kartı
 * zaten burada: düzenleme de burada.
 *
 * ================================================================
 * BOŞ ALAN "TEMİZLE" DEMEK — VE SUNUCU DA BÖYLE ANLIYOR
 * ================================================================
 * Kullanıcı bir hedefi kaldırmak istediğinde yaptığı şey alanı
 * boşaltmak. Boş alan `null` olarak gönderiliyor; sunucu `null`u
 * "temizle", alanın yokluğunu "dokunma" olarak ayırıyor
 * (`apps/api/.../hedef.ts`). İki anlamı karıştırmak, silme isteğini
 * sessizce yok sayardı.
 */

/** `ExamKind` — sunucudaki `SINAV_TURLERI` ile AYNI sıra ve AYNI değerler. */
const SINAV_ADI: Record<string, string> = {
  TYT: "TYT",
  AYT: "AYT",
  YDT: "YDT (Yabancı Dil)",
  LGS: "LGS",
  BRANCH: "Branş sınavı",
  KPSS: "KPSS",
  DGS: "DGS",
  OTHER: "Diğer",
};

/** `Date`i `YYYY-AA-GG`ye çevirir — `<input type="date">` bunu bekliyor. */
function tarihAlani(iso: string | null): string {
  return iso === null ? "" : iso.slice(0, 10);
}

/** Boş metin `null` — "temizle". Dolu metin olduğu gibi. */
function bosNull(d: string): string | null {
  const k = d.trim();
  return k.length === 0 ? null : k;
}

/**
 * Boş sayı alanı `null`; geçersiz sayı `undefined` (DOKUNMA).
 *
 * `Number("abc")` `NaN` üretiyor ve `NaN` gönderilseydi sunucu
 * `finite()` ile reddederdi — ama kullanıcı neyin yanlış olduğunu
 * anlamazdı. Geçersiz girdi hiç gönderilmiyor; alan zaten `type
 *="number"` olduğu için tarayıcı da çoğunu eliyor.
 */
function sayi(d: string): number | null | undefined {
  const k = d.trim();
  if (k.length === 0) return null;
  const n = Number(k);
  return Number.isFinite(n) ? n : undefined;
}

export function HedefFormu({
  ogrenciId,
  hedef,
  alan,
  onKapat,
}: {
  ogrenciId: string;
  hedef: OgrenciKunyesi["hedef"];
  /** F-PPL-05c — alan hedefle aynı kartta: ikisi de "neye hazırlanıyor" sorusu. */
  alan: OgrenciKunyesi["alan"];
  onKapat: () => void;
}) {
  const qc = useQueryClient();
  const [alanId, setAlanId] = useState(alan?.id ?? "");
  const [sinav, setSinav] = useState(hedef.sinav ?? "");
  const [puan, setPuan] = useState(hedef.puan === null ? "" : String(hedef.puan));
  const [siralama, setSiralama] = useState(hedef.siralama === null ? "" : String(hedef.siralama));
  const [okul, setOkul] = useState(hedef.okul ?? "");
  const [tarih, setTarih] = useState(tarihAlani(hedef.sinavTarihi));

  const mut = useMutation({
    mutationFn: () => {
      /*
        YALNIZCA DEĞİŞEN alanlar gönderiliyor. Hepsini göndermek
        denetim kaydına değişmemiş alanları da yazdırırdı —
        `OgrenciDosyasi.tsx`teki profil formu da aynı kuralı izliyor.
      */
      const veri: HedefGirdisi & { fieldId?: string | null } = {};
      if ((alan?.id ?? "") !== alanId) veri.fieldId = alanId === "" ? null : alanId;
      if ((hedef.sinav ?? "") !== sinav) veri.targetExamKind = bosNull(sinav);
      if ((hedef.puan === null ? "" : String(hedef.puan)) !== puan) {
        const s = sayi(puan);
        if (s !== undefined) veri.targetScore = s;
      }
      if ((hedef.siralama === null ? "" : String(hedef.siralama)) !== siralama) {
        const s = sayi(siralama);
        if (s !== undefined) veri.targetRank = s;
      }
      if ((hedef.okul ?? "") !== okul) veri.targetSchool = bosNull(okul);
      if (tarihAlani(hedef.sinavTarihi) !== tarih) veri.examDate = bosNull(tarih);
      return ogrenciProfilGuncelle(ogrenciId, veri);
    },
    onSuccess: async () => {
      // Kunye sorgusu tazeleniyor: kart eski degeri gostermeye devam
      // etseydi kullanici kaydinin gitmedigini SANIRDI.
      await qc.invalidateQueries({ queryKey: ["ogrenci-kunye", ogrenciId] });
      // Program ekraninin ders listesi alana bagli (F-SCH-03b).
      await qc.invalidateQueries({ queryKey: ["ders-havuzu", ogrenciId] });
      onKapat();
    },
  });

  const degisti =
    (alan?.id ?? "") !== alanId ||
    (hedef.sinav ?? "") !== sinav ||
    (hedef.puan === null ? "" : String(hedef.puan)) !== puan ||
    (hedef.siralama === null ? "" : String(hedef.siralama)) !== siralama ||
    (hedef.okul ?? "") !== okul ||
    tarihAlani(hedef.sinavTarihi) !== tarih;

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (degisti) mut.mutate();
      }}
    >
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <AlanSecimi deger={alanId} onChange={setAlanId} mevcut={alan} />
        <Secim etiket="Hedef sınav" value={sinav} onChange={(e) => setSinav(e.target.value)}>
          {/* Bos secenek "hedef yok" demek — silme yolu olmadan hedef kalici olurdu. */}
          <option value="">Seçilmedi</option>
          {Object.entries(SINAV_ADI).map(([deger, ad]) => (
            <option key={deger} value={deger}>
              {ad}
            </option>
          ))}
        </Secim>

        <Alan
          etiket="Hedef puan"
          type="number"
          inputMode="decimal"
          step="0.01"
          min="0"
          value={puan}
          onChange={(e) => setPuan(e.target.value)}
          /*
            UST SINIR YOK — ve bu bilincli. Sinir SINAV TURUNE gore
            degisiyor (TYT/AYT ~100-560, KPSS 0-100, LGS yuzdelik) ve
            tur bazli tablo bu depoda hicbir yerde yazili degil.
            Tek bir sayi yazmak bazi turlerde GECERLI hedefleri
            reddederdi.
          */
          yardim="Sınav türüne göre değişir; üst sınır uygulanmıyor."
        />

        <Alan
          etiket="Hedef sıralama"
          type="number"
          inputMode="numeric"
          step="1"
          min="1"
          value={siralama}
          onChange={(e) => setSiralama(e.target.value)}
        />

        <Alan
          etiket="Hedef okul"
          value={okul}
          onChange={(e) => setOkul(e.target.value)}
          maxLength={200}
        />

        <Alan
          etiket="Sınav tarihi"
          type="date"
          value={tarih}
          onChange={(e) => setTarih(e.target.value)}
          /*
            GECMIS TARIH ENGELLENMIYOR: sinav gectiyse tarih hâlâ dogru
            bilgi ve kunye "kac gun once" diye yaziyor. Ileriye
            kisitlamak, gecmis donemin kaydini duzeltmeyi imkânsiz
            kilardi.
          */
          yardim="Boş bırakılırsa sınava kalan gün gösterilmez."
        />
      </div>

      <p className="text-notr-600 text-xs">
        Bir alanı boşaltmak o hedefi <strong>siler</strong>. Değişiklik denetim kaydına geçer.
      </p>

      {mut.isError && (
        <p role="alert" className="text-hata-700 text-sm font-medium">
          {mut.error instanceof ApiHatasi ? mut.error.message : "Hedef güncellenemedi."}
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        <Buton type="submit" yukleniyor={mut.isPending} disabled={!degisti}>
          Kaydet
        </Buton>
        <Buton gorunum="ikincil" type="button" onClick={onKapat}>
          Vazgeç
        </Buton>
      </div>
    </form>
  );
}
